import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useCompanyAiAgents } from '../../hooks/useCompanyAiAgents';
import {
  CUSTOMER_SUCCESS_AGENT_ID,
  customerSuccessDocId,
} from '../../lib/aiWorkforceRoster';
import {
  REPORT_ISSUE_CATEGORIES,
  captureElementSnapshot,
} from '../../lib/reportIssueRouting';

const createHqElevateIntake = httpsCallable(functions, 'createHqElevateIntake');

export const SHIFTYPM_HQ_COMPANY_ID = 'SHIFTYPM_HQ';
export const EXTERNAL_SUPPORT_TICKET_TYPE = 'external_support';
export const DEVELOPMENT_TICKET_TYPE = 'development';

/** Agentic Gateway: all Report Issue traffic wakes CSA only. */
const CSA_TARGET_ROLE = 'Customer Success Agent';
const REPORT_ISSUE_WIDGET_Z_INDEX = 2147483000;

/**
 * Find the Customer Success Agent on an HQ roster (flags, role/name heuristics, known ids).
 * @param {Array<object> | null | undefined} roster
 * @returns {object | null}
 */
function findCsaAgent(roster) {
  const agents = Array.isArray(roster) ? roster : [];
  const expectedDocId = customerSuccessDocId(SHIFTYPM_HQ_COMPANY_ID);

  const byFlag = agents.find(
    (worker) =>
      worker?.isCustomerSuccess === true ||
      worker?.rosterRole === CUSTOMER_SUCCESS_AGENT_ID ||
      worker?.id === CUSTOMER_SUCCESS_AGENT_ID ||
      worker?.id === expectedDocId ||
      (typeof worker?.id === 'string' &&
        (worker.id === 'CSA' ||
          worker.id.startsWith('csa_') ||
          /CSA/i.test(worker.id))),
  );
  if (byFlag) return byFlag;

  return (
    agents.find((worker) => {
      const role = String(
        worker?.roleTitle || worker?.role || worker?.rosterRole || '',
      );
      const name = String(worker?.name || worker?.agentName || '');
      return (
        /CSA/i.test(role) ||
        /CSA/i.test(name) ||
        /customer\s*success/i.test(role) ||
        /customer\s*success/i.test(name)
      );
    }) || null
  );
}

const STEPS = {
  INTRO: 'intro',
  CATEGORY: 'category',
  ELEMENT: 'element',
  DETAILS: 'details',
  REVIEW: 'review',
  DONE: 'done',
};

/**
 * Global Report Issue / CSA intake FAB + side panel.
 * Mount once in Dashboard shell so it appears on every tab.
 * @param {{ userData?: object | null, user?: object | null }} props
 */
export default function ReportIssueWidget({ userData = null, user = null }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.INTRO);
  const [picking, setPicking] = useState(false);
  const [category, setCategory] = useState('ui');
  const [summary, setSummary] = useState('');
  const [reproSteps, setReproSteps] = useState('');
  const [consolePaste, setConsolePaste] = useState('');
  const [elementSnap, setElementSnap] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const pickHandlerRef = useRef(null);

  const { workspace, companyId: workspaceCompanyId } = useWorkspace();
  const rosterCompanyId =
    workspaceCompanyId ||
    userData?.companyId ||
    workspace?.id ||
    SHIFTYPM_HQ_COMPANY_ID;

  const { agents: rosterAgents, loading: rosterLoading } =
    useCompanyAiAgents(rosterCompanyId);

  /** Prefer company-configured triage agent; fall back to CSA heuristics. */
  const csaAgent = useMemo(() => {
    const configuredId =
      typeof workspace?.defaultTriageAgentId === 'string'
        ? workspace.defaultTriageAgentId.trim()
        : '';
    if (configuredId) {
      const configured = (Array.isArray(rosterAgents) ? rosterAgents : []).find(
        (a) => a.id === configuredId,
      );
      if (configured) return configured;
    }
    return findCsaAgent(rosterAgents);
  }, [rosterAgents, workspace?.defaultTriageAgentId]);

  const [selectedCsaAgentId, setSelectedCsaAgentId] = useState('');

  useEffect(() => {
    setSelectedCsaAgentId(csaAgent?.id || '');
  }, [csaAgent?.id]);

  const csaReady = Boolean(selectedCsaAgentId && csaAgent?.id);

  const stopPicking = () => {
    setPicking(false);
    if (pickHandlerRef.current) {
      document.removeEventListener('click', pickHandlerRef.current, true);
      pickHandlerRef.current = null;
    }
    document.body.style.cursor = '';
  };

  useEffect(() => () => stopPicking(), []);

  useEffect(() => {
    if (!picking) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') stopPicking();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picking]);

  const resetFlow = () => {
    stopPicking();
    setStep(STEPS.INTRO);
    setCategory('ui');
    setSummary('');
    setReproSteps('');
    setConsolePaste('');
    setElementSnap(null);
    setError('');
    setResult(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    stopPicking();
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
    if (step === STEPS.DONE) resetFlow();
  };

  const startElementPick = () => {
    setError('');
    setOpen(false);
    setPicking(true);
    document.body.style.cursor = 'crosshair';

    const onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-report-issue-widget="true"]')) return;

      const snap = captureElementSnapshot(target);
      setElementSnap({
        ...snap,
        pathname: window.location.pathname,
        pageHref: window.location.href,
        capturedAt: new Date().toISOString(),
      });
      stopPicking();
      setOpen(true);
      setStep(STEPS.DETAILS);
    };

    pickHandlerRef.current = onClick;
    window.setTimeout(() => {
      document.addEventListener('click', onClick, true);
    }, 0);
  };

  const buildTicketBodies = () => {
    const catLabel =
      REPORT_ISSUE_CATEGORIES[category]?.label || category || 'Other';
    const browser = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const lines = [
      '## CSA intake (Report Issue widget)',
      '',
      `**Problem type:** ${catLabel}`,
      `**Reporter:** ${userData?.email || user?.email || user?.uid || 'unknown'}`,
      `**Reporter company:** ${userData?.companyId || '—'}`,
      `**Route:** ${elementSnap?.pathname || window.location.pathname}`,
      `**Browser:** ${browser}`,
      '',
      '### Summary',
      summary.trim() || '—',
      '',
      '### Steps to reproduce',
      reproSteps.trim() || '—',
      '',
    ];

    if (elementSnap) {
      lines.push(
        '### Selected UI element',
        `- Tag: \`${elementSnap.tagName}\``,
        elementSnap.id ? `- Id: \`#${elementSnap.id}\`` : null,
        elementSnap.className ? `- Class: \`${elementSnap.className}\`` : null,
        elementSnap.domPath ? `- Path: \`${elementSnap.domPath}\`` : null,
        elementSnap.textSnippet
          ? `- Text: "${elementSnap.textSnippet}"`
          : null,
        '',
        '### Element HTML (truncated)',
        '```html',
        elementSnap.outerHtml || '(empty)',
        '```',
        '',
      );
    }

    if (consolePaste.trim()) {
      lines.push(
        '### Console / DevTools paste',
        '```',
        consolePaste.trim().slice(0, 6000),
        '```',
        '',
      );
    }

    lines.push(
      '### Operator notes for CSA (Agentic Gateway)',
      'Triage from the route + element snapshot, then dispatch the correct engineering agent.',
      'Prefer a minimal code_sandbox patch when escalating. Do not invent files.',
    );

    const body = lines.filter((l) => l !== null).join('\n');
    const titleBase =
      summary.trim().slice(0, 80) ||
      (elementSnap?.textSnippet
        ? `Issue near "${elementSnap.textSnippet.slice(0, 40)}"`
        : `In-app report (${catLabel})`);

    return { body, title: titleBase };
  };

  const handleSubmit = async () => {
    if (!summary.trim()) {
      setError('Add a short summary of the problem.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const { body, title } = buildTicketBodies();

      const result = await createHqElevateIntake({
        summary: summary.trim(),
        title,
        body,
        category,
        priority: 'medium',
        reportIssueMeta: {
          category,
          element: elementSnap || null,
          pathname: window.location.pathname,
          assigneeAgentName:
            csaAgent?.name || csaAgent?.agentName || CSA_TARGET_ROLE,
        },
      });
      const data = result?.data || {};
      if (!data.ok || !data.ticketId) {
        throw new Error(data.message || 'Could not create support request.');
      }

      setResult({
        supportTicketId: data.ticketId,
        csaName:
          csaAgent?.name || csaAgent?.agentName || CSA_TARGET_ROLE,
        csaAgentId: data.proposedCsaAgentId || selectedCsaAgentId || '',
        pendingApproval: data.csaApprovalStatus === 'pending',
        reporterRole: data.reporterRole || userData?.role || '',
      });
      setStep(STEPS.DONE);
    } catch (err) {
      console.error('ReportIssueWidget submit:', err);
      setError(
        err?.message ||
          err?.details ||
          'Could not create support ticket.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {picking ? (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: REPORT_ISSUE_WIDGET_Z_INDEX + 1,
            pointerEvents: 'none',
            border: '3px dashed',
            borderColor: 'primary.main',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Chip
            color="primary"
            icon={<AdsClickIcon />}
            label="Click any UI element to attach it — Esc to cancel"
            sx={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              pointerEvents: 'auto',
              fontWeight: 700,
            }}
            onDelete={stopPicking}
          />
        </Box>
      ) : null}

      <Box
        data-report-issue-widget="true"
        sx={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: REPORT_ISSUE_WIDGET_Z_INDEX,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <Collapse in={open} orientation="vertical" unmountOnExit>
          <Box
            sx={{
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: REPORT_ISSUE_WIDGET_Z_INDEX,
              width: { xs: 'min(100vw - 32px, 400px)', sm: 400 },
              maxHeight: 'min(72vh, 640px)',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              boxShadow: 8,
              overflow: 'hidden',
              mb: 1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <SupportAgentIcon fontSize="small" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  Report Issue · CSA
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Triage &amp; route to AI workers
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ color: 'inherit' }}
                aria-label="Close report issue"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
              {error ? (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5 }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              ) : null}

              {step === STEPS.INTRO ? (
                <Stack spacing={1.5}>
                  <Typography variant="body2">
                    Hi — I&apos;m the Customer Success Agent. I&apos;ll ask a few
                    questions, help you capture the broken UI element, then wake
                    myself to triage and dispatch the right engineering team.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setStep(STEPS.CATEGORY)}
                    sx={{ textTransform: 'none' }}
                  >
                    Start intake
                  </Button>
                </Stack>
              ) : null}

              {step === STEPS.CATEGORY ? (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    What kind of problem is this?
                  </Typography>
                  <FormControl>
                    <RadioGroup
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {Object.entries(REPORT_ISSUE_CATEGORIES).map(
                        ([id, meta]) => (
                          <FormControlLabel
                            key={id}
                            value={id}
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2">
                                {meta.label}
                              </Typography>
                            }
                          />
                        ),
                      )}
                    </RadioGroup>
                  </FormControl>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    All reports route to the Customer Success Agent (CSA) via the
                    Agentic Gateway.
                  </Alert>
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => setStep(STEPS.INTRO)}
                      sx={{ textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setStep(STEPS.ELEMENT)}
                      sx={{ textTransform: 'none' }}
                    >
                      Continue
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {step === STEPS.ELEMENT ? (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Point at the broken UI
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click <strong>Select element</strong>, then click anything in
                    the app. Optional: open DevTools → Elements, copy the node,
                    and paste it on the next step.
                  </Typography>
                  {elementSnap ? (
                    <Alert severity="success" sx={{ py: 0.5 }}>
                      Captured <code>{elementSnap.tagName}</code>
                      {elementSnap.domPath
                        ? ` · ${elementSnap.domPath.slice(0, 80)}`
                        : ''}
                    </Alert>
                  ) : null}
                  <Button
                    variant="outlined"
                    startIcon={<AdsClickIcon />}
                    onClick={startElementPick}
                    sx={{ textTransform: 'none' }}
                  >
                    {elementSnap ? 'Re-select element' : 'Select element'}
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => setStep(STEPS.CATEGORY)}
                      sx={{ textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setStep(STEPS.DETAILS)}
                      sx={{ textTransform: 'none' }}
                    >
                      {elementSnap ? 'Continue' : 'Skip for now'}
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {step === STEPS.DETAILS ? (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Tell CSA what went wrong
                  </Typography>
                  <TextField
                    label="Short summary"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    fullWidth
                    size="small"
                    required
                    multiline
                    minRows={2}
                  />
                  <TextField
                    label="Steps to reproduce"
                    value={reproSteps}
                    onChange={(e) => setReproSteps(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    placeholder={
                      '1. Go to …\n2. Click …\n3. See …'
                    }
                  />
                  <TextField
                    label="Optional: paste element HTML from DevTools"
                    value={consolePaste}
                    onChange={(e) => setConsolePaste(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    placeholder="Right-click element → Copy → Copy outerHTML"
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => setStep(STEPS.ELEMENT)}
                      sx={{ textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setStep(STEPS.REVIEW)}
                      disabled={!summary.trim()}
                      sx={{ textTransform: 'none' }}
                    >
                      Review
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {step === STEPS.REVIEW ? (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    HQ human approval first
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your request is filed to ShiftyPM HQ for human approval.
                    After approval, the Customer Success Agent (CSA) is assigned
                    and queued — it does not wake AI workers automatically.
                  </Typography>
                  {userData?.role ? (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Reporter role stamped on the ticket:{' '}
                      <strong>{userData.role}</strong>
                    </Alert>
                  ) : null}
                  <Divider />
                  <Typography variant="caption" color="text.secondary">
                    Category: {REPORT_ISSUE_CATEGORIES[category]?.label}
                    {elementSnap ? ` · Element: ${elementSnap.tagName}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => setStep(STEPS.DETAILS)}
                      disabled={submitting}
                      sx={{ textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      onClick={handleSubmit}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Submit for HQ approval
                    </Button>
                  </Stack>
                </Stack>
              ) : null}

              {step === STEPS.DONE && result ? (
                <Stack spacing={1.5}>
                  <Alert severity="success">
                    Request submitted for HQ approval
                    {result.reporterRole
                      ? ` (role: ${result.reporterRole})`
                      : ''}
                    . CSA will only run after a human approves it in Support
                    Inbox.
                  </Alert>
                  <Typography variant="body2">
                    Support: <code>{result.supportTicketId}</code>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Status: pending HQ approval → then queued to{' '}
                    {CSA_TARGET_ROLE}.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={resetFlow}
                    sx={{ textTransform: 'none' }}
                  >
                    Report another issue
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </Box>
        </Collapse>

        <Tooltip title={open ? 'Close' : 'Report issue to CSA'} placement="left">
          <Fab
            color="primary"
            size="medium"
            onClick={() => (open ? handleClose() : handleOpen())}
            aria-label="Report issue"
            sx={{ pointerEvents: 'auto' }}
          >
            {open ? <CloseIcon /> : <ConfirmationNumberOutlinedIcon />}
          </Fab>
        </Tooltip>
      </Box>
    </>
  );
}
